// Roster import pipeline: extract → match → verify → apply.
//
// Matching is intentionally simple: case-insensitive exact name lookup
// against the owner first, then the roster. "Rick H" and "Rick H." won't
// match without explicit user help — no fuzzy library at this stage.
//
// Apply rules (immutable contract):
//   - Owner matched → ONLY handicapIndex is updated on the owner record.
//     Owner name + email never changed by import.
//   - Existing roster player matched → ONLY handicapIndex is updated.
//     Names, emails, ids are never touched.
//   - No match → a new roster row is created with name + handicapIndex.
//   - Unselected rows are skipped.
//   - Nothing is ever deleted.

import {
  RosterOwner,
  RosterPlayer,
  addRosterPlayer,
  addRosterAlias,
  loadOwner,
  saveOwner,
  updateRosterPlayer,
} from "./roster";

export interface ExtractedPlayer {
  name: string;
  handicapIndex: number;
}

export type MatchType = "update" | "update-owner" | "new";

export interface MatchedRow {
  detectedName: string;
  handicapIndex: number;
  matchType: MatchType;
  // Set when matchType === "update" — the roster row that will receive
  // the new handicapIndex. Not used for owner / new.
  existingId?: string;
  // Set by the import review UI when the user links a "new" (unmatched) row to
  // an existing roster player. Overrides the add-new behavior: update that
  // player's handicap and remember detectedName as an alias for next time.
  linkToId?: string;
  // Default true; user can uncheck individual rows to skip them on apply.
  selected: boolean;
}

export function matchAgainstRoster(
  extracts: ExtractedPlayer[],
  roster: RosterPlayer[],
  owner?: RosterOwner | null,
): MatchedRow[] {
  const ownerName = owner?.name.trim().toLowerCase();
  return extracts.map((extract) => {
    const target = extract.name.trim().toLowerCase();
    if (ownerName && target === ownerName) {
      return {
        detectedName: extract.name.trim(),
        handicapIndex: extract.handicapIndex,
        matchType: "update-owner",
        selected: true,
      };
    }
    // Match the current name OR any stored alias, so a shortened display name
    // (e.g. "Rick") still matches the imported full name ("RICHARD HORODYSKI").
    const existing = roster.find(
      (r) =>
        r.name.trim().toLowerCase() === target ||
        (r.aliases || []).some((a) => a.trim().toLowerCase() === target),
    );
    return {
      detectedName: extract.name.trim(),
      handicapIndex: extract.handicapIndex,
      matchType: existing ? "update" : "new",
      existingId: existing?.id,
      selected: true,
    };
  });
}

export interface ApplyResult {
  updated: number;
  added: number;
  ownerUpdated: boolean;
  skipped: number;
}

export function applyImports(rows: MatchedRow[]): ApplyResult {
  let updated = 0;
  let added = 0;
  let ownerUpdated = false;
  let skipped = 0;
  for (const row of rows) {
    if (!row.selected) {
      skipped++;
      continue;
    }
    if (row.matchType === "update-owner") {
      const owner = loadOwner();
      if (owner) {
        // Only handicapIndex changes — name / email preserved.
        saveOwner({ ...owner, handicapIndex: row.handicapIndex });
        ownerUpdated = true;
      }
      continue;
    }
    // Auto-matched player, OR a "new" row the user linked to an existing one.
    // Only handicapIndex changes — names, ids preserved.
    const targetId =
      row.matchType === "update" ? row.existingId : row.linkToId;
    if (targetId) {
      updateRosterPlayer(targetId, { handicapIndex: row.handicapIndex });
      // Remember this detected name so future imports auto-match. No-op when it
      // already matched by name/alias; the win is the user-linked case, where
      // it teaches the matcher that this full name IS this (renamed) player.
      addRosterAlias(targetId, row.detectedName);
      updated++;
      continue;
    }
    if (row.matchType === "new") {
      addRosterPlayer({
        name: row.detectedName,
        handicapIndex: row.handicapIndex,
        aliases: [row.detectedName],
      });
      added++;
    }
  }
  return { updated, added, ownerUpdated, skipped };
}
